<template>
  <el-drawer
    :close-on-click-modal="true"
    :close-on-press-escape="false"
    :with-header="false"
    append-to-body
    v-model="drawer"
    size="480"
    modal-class="hotelux-member-drawer"
  >
    <div class="member-header">
      <div class="close-btn" @click="drawer = false">
        <img class="close" src="https://uploads.ytripos.com/close.png" />
        <span class="close-text">{{ $t("shared.common.Close") }}</span>
      </div>
    </div>

    <div class="member-content">
      <div class="user-profile">
        <img class="user-avatar" :src="avatarUrl" />
        <div class="user-info">
          <div class="user-name-row">
            {{ user?.name }}
            <img
              v-if="memberLevel"
              class="member-badge"
              :src="memberLevel"
              alt=""
            />
          </div>
          <div class="user-email">{{ user?.email }}</div>
        </div>
      </div>

      <div class="divider" />

      <div class="menu-list">
        <template v-for="(item, index) in menuItems" :key="index">
          <div class="menu-item" @click="handleMenuClick(item)">
            <div class="menu-item-content">
              <font-icon :icon="item.icon" class="icon-hotelux menu-icon" />
              <span class="menu-text">{{ $t(item.label) }}</span>
            </div>
            <font-icon
              v-if="item.hasArrow && !item.children"
              icon="icon-hotelux-youjiantou"
              class="icon-hotelux arrow-icon"
            />
          </div>

          <div v-if="item.children" class="submenu">
            <div
              v-for="(child, i) in item.children"
              :key="i"
              class="submenu-item"
              @click.stop="handleMenuClick(child)"
            >
              <span class="submenu-text">{{ $t(child.label) }}</span>
              <font-icon
                icon="icon-hotelux-youjiantou"
                class="icon-hotelux arrow-icon"
              />
            </div>
          </div>
        </template>
      </div>

      <!-- <div class="logout-btn" @click="handleLogout">
        {{ $t("shared.auth.logout") }}
      </div> -->
    </div>
  </el-drawer>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { computed, watch } from "vue";
import { useRouter } from "vue-router";

import { useYChannelStoreHook } from "@/distributions/consumer/y/store/channel";
import { useUserStoreHook } from "@/store/modules/user";

import defaultAvatar from "./icon-head.png";

const drawer = defineModel("show");

const props = defineProps({
  settings: {
    type: Array,
    default: () => []
  }
});

const router = useRouter();
const userStore = useUserStoreHook();
const yChannelStore = useYChannelStoreHook();
const { user } = storeToRefs(userStore);

const avatarUrl = computed(() => {
  return user.value?.photoUrls?.[0] || defaultAvatar;
});

const memberLevel = computed(() => {
  return yChannelStore.configs?.level[
    user.value?.loyaltyProgram?.ref?.type || "guest"
  ];
});

const menuItems = computed(() => {
  const levelRefType = user.value?.loyaltyProgram?.ref?.type || "";
  const showUpgrade = !levelRefType || levelRefType === "guest";

  const items = props.settings
    .filter(item => {
      if (item.show) {
        return item.show(user.value);
      }
      return !item.label?.includes("logout");
    })
    .map(item => ({
      ...item,
      icon: item.path === "/orders" ? "icon-hotelux-Order" : item.icon,
      label: item.path === "/orders" ? "shared.common.orderList" : item.label,
      hasArrow: true
    }));
  if (showUpgrade) {
    items.push({
      label: "shared.auth.upgradeMembership",
      icon: "icon-hotelux-Upgrade",
      hasArrow: true,
      children: [
        {
          label: "shared.auth.creditCard.promotions",
          path: "/credit-card-match"
        },
        {
          label: "shared.contactInfo.airlineMatch.title",
          path: "/airline-match"
        }
      ]
    });
  }
  items.push({
    icon: "icon-hotelux-Logout",
    label: "shared.auth.logout",
    type: "not-close",
    onClick: handleLogout
  });
  return items;
});

const handleMenuClick = item => {
  if (item.children) return;

  drawer.value = false;

  if (item.path) {
    router.push({ path: item.path });
  }
  if (typeof item.onClick === "function") {
    item.onClick();
  }
};

const handleLogout = () => {
  const logoutItem = props.settings.find(item =>
    item.label?.includes("logout")
  );
  if (logoutItem && typeof logoutItem.onClick === "function") {
    logoutItem.onClick();
  }
};

watch(
  () => drawer.value,
  async val => {
    if (val) {
      await userStore.getUser();
    }
  }
);

defineOptions({
  name: "HoteluxMemberDrawer"
});
</script>

<style lang="scss" scoped>
.hotelux-member-drawer {
  :deep(.el-drawer) {
    --el-drawer-padding-primary: 0;
  }
}

.member-header {
  padding: 52px 36px 32px;

  .close-btn {
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }
  .close {
    height: 14px;
    cursor: pointer;
    margin-right: 12px;
  }
  .close-icon {
    font-size: 14px;
    margin-right: 12px;
    color: #212223;
  }

  .close-text {
    font-family: Lato, -apple-system, sans-serif;
    font-size: 10px;
    font-weight: 700;
    color: #212223;
    line-height: 18px;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
}

.member-content {
  padding: 0 36px 24px;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.user-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
}

.user-info {
  flex: 1;
}

.user-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Cormorant, var(--y-title-font), serif;
  font-size: 20px;
  font-weight: 700;
  color: #212223;
  line-height: 1.2;
}

.member-badge {
  height: 18px;
  width: auto;
}

.user-email {
  margin-top: 6px;
  font-family: Lato, -apple-system, sans-serif;
  font-size: 12px;
  color: #757575;
  line-height: 1.5;
}

.divider {
  width: 100%;
  height: 1px;
  background: #f1f2f3;
  margin-bottom: 26px;
}

.menu-list {
  margin-bottom: 64px;
}

.menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
  height: 44px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
}

.menu-item-content {
  display: flex;
  align-items: center;
  padding: 10px 0;
}

.menu-icon {
  font-size: 20px;
  margin-right: 12px;
  color: #212223;
}

.menu-text {
  font-family: "lato-bold";
  font-size: 14px;
  font-weight: 400;
  color: #212223;
  line-height: 1;
}

.arrow-icon {
  font-size: 10px;
  color: #212223;
}

.submenu {
  margin-left: 32px;
  margin-top: -12px;
  margin-bottom: 20px;
}

.submenu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // margin-bottom: 22px;
  margin-top: 12px;
  padding: 10px 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
}

.submenu-text {
  display: inline-block;
  font-family: Lato, -apple-system, sans-serif;
  font-size: 14px;
  height: 24px;
  font-weight: 400;
  color: #757575;
}

.logout-btn {
  padding: 17px 0;
  font-family: Lato, -apple-system, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #212223;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  border: 1px solid #d8d8d8;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #f8f8f8;
    border-color: #212223;
  }
}
</style>
